// Location helpers for smart search. Two tiers, both optional — search works with neither:
//   Tier 1: server resolves a coarse city/coords from the visitor's IP (no permission) — see api.js.
//   Tier 2: precise GPS via navigator.geolocation, only when the user explicitly asks for it.
// The last confirmed location is remembered so returning visitors skip the round-trips.

const STORAGE_KEY = 'sqip.location';

/** Persisted location: { lat, lng, city, state, country, source } or null. */
export function loadSavedLocation() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function saveLocation(loc) {
  try {
    if (loc) localStorage.setItem(STORAGE_KEY, JSON.stringify(loc));
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* storage blocked — ignore */
  }
}

/**
 * Precise GPS (Tier 2). Resolves { lat, lng } or rejects.
 * ALWAYS triggers the browser permission prompt — call it only from an explicit user action.
 */
export function getBrowserLocation({ timeout = 10000 } = {}) {
  return new Promise((resolve, reject) => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      reject(new Error('Geolocation not supported'));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      (err) => reject(err), // includes PERMISSION_DENIED — caller stays on Tier 1, no re-prompt
      { enableHighAccuracy: false, timeout, maximumAge: 5 * 60 * 1000 },
    );
  });
}

/**
 * Reverse-geocode GPS coords to a city/state/country label for DISPLAY ONLY (the distance math uses
 * the raw coords, so a wrong label never corrupts ranking). Keyless BigDataCloud client endpoint.
 * Returns { city, state, country } or {} on any failure.
 */
export async function reverseGeocode(lat, lng) {
  try {
    const url =
      `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${lat}&longitude=${lng}&localityLanguage=en`;
    const res = await fetch(url);
    if (!res.ok) return {};
    const d = await res.json();
    return {
      city: d.city || d.locality || '',
      state: d.principalSubdivision || '',
      country: d.countryName || '',
    };
  } catch {
    return {};
  }
}
